import { SingleNote } from '@textbook-api-client/lib/interface/single-note.model';
import { first, withLatestFrom } from 'rxjs/operators';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  ElementRef,
  EventEmitter,
  HostBinding,
  Inject,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import {
  asyncScheduler,
  combineLatest,
  filter,
  from,
  fromEvent,
  map,
  mergeMap,
  Observable,
  subscribeOn,
  Subscription,
} from 'rxjs';
import { DrawerTextComponent } from '../drawer-text/drawer-text.component';
import { ActionNotesService, UtilitiesService } from '../../../actions/@base/services';
import { InternalSingleNoteState, NoteStatus, SaveStatus } from '../../../actions/@base/models';
import {
  TextbookActionsHandler,
  TextbookActionsHandlerToken,
} from '../../../../external/providers/textbook-actions-handler';
import { DrawerTextMobileComponent } from "../drawer-text-mobile/drawer-text-mobile.component";
import { AbstractDrawerTextComponent } from "../drawer-text/abstract-drawer-text.component";
import { OperatingSystem } from "../../../../external/models";

const MOBILE_OPERATING_SYSTEMS: OperatingSystem[] = [OperatingSystem.Android, OperatingSystem.Ios];

interface InternalNoteState {
  id: string;
  viewRef: ComponentRef<AbstractDrawerTextComponent>;
}

type Subset<K> = {
  [attr in keyof K]?: K[attr] extends object ? Subset<K[attr]> : K[attr];
};

interface MouseCoords {
  x: number;
  y: number;
}

@Component({
  selector: 'txtb-drawer-texts',
  templateUrl: './drawer-texts.component.html',
  styleUrls: ['./drawer-texts.component.scss'],
  standalone: false,
})
export class DrawerTextsComponent implements AfterViewInit, OnDestroy {
  @Input() currentZoom$!: Observable<number|null>;
  @Input() isActive$!: Observable<boolean>;
  @Input() isVisible$!: Observable<boolean>;
  @Input() isDisabled$!: Observable<boolean>;
  @Input() textColor: string|null = '#000000';
  @Output() isTextEdited = new EventEmitter<boolean>();
  @Output() isTextToolActive = new EventEmitter<boolean>();
  @Output() isActive = new EventEmitter<boolean>();
  @Output() zoomDisabled = new EventEmitter<boolean>();
  @ViewChild('notesContainer', { read: ViewContainerRef }) wrapperViewRef!: ViewContainerRef;

  subscription = new Subscription();
  activeNotes: InternalNoteState[] = [];
  boundsScale = 0.95;
  pageDomRect: DOMRect|null|undefined;
  textBoxes!: Observable<SingleNote[]>;

  @HostBinding('class.is-active') active = false;
  @HostBinding('class.is-disabled') isDisabled = false;
  @HostBinding('class.is-hidden') isHidden = false;
  @HostBinding('class') notesBoundary = 'notes-boundary';
  private readonly isOnlineMode$ = this.textbookActionsHandler.isReadonlyMode$.pipe(
    map((readonly) => !readonly)
  );

  constructor(
    private readonly changeDetector: ChangeDetectorRef,
    private readonly componentFactoryResolver: ComponentFactoryResolver,
    private readonly hostRef: ElementRef,
    @Inject(TextbookActionsHandlerToken) public readonly textbookActionsHandler: TextbookActionsHandler,
    private readonly utilitiesService: UtilitiesService,
    private readonly actionNotesService: ActionNotesService
  ) {
  }

  ngAfterViewInit(): void {
    this.subscription.add(
      combineLatest([this.isActive$, this.isOnlineMode$, this.isDisabled$]).subscribe(([isActive, isOnlineMode, isDisabled]) => {
        this.active = isActive;
        this.isDisabled = !isActive || !isOnlineMode || isDisabled;
      })
    );

    this.subscription.add(this.isVisible$.subscribe((visibility) => (this.isHidden = !visibility)));

    this.subscription.add(
      this.actionNotesService
        .currentNotesState$()
        .pipe(subscribeOn(asyncScheduler))
        .subscribe((notes) => this.initializeNotes(notes))
    );

    const { nativeElement: wrapper } = this.hostRef;

    const click$ = from(['mousedown', 'touchstart']).pipe(
      mergeMap((event) => fromEvent<MouseEvent|TouchEvent>(wrapper, event)),
      filter(() => this.active),
      map((e) => {
        e.preventDefault();
        return this.getMousePosition(e, wrapper);
      }),
      withLatestFrom(this.currentZoom$, this.isOnlineMode$)
    );

    this.subscription.add(
      click$.subscribe(([mouseCoords, currentZoom, isOnlineMode]) => {
        this.updatePageBounds();
        if (!this.pageDomRect || !isOnlineMode) {
          return;
        }
        this.onMouseDown(mouseCoords, currentZoom, this.wrapperViewRef, this.pageDomRect);
      })
    );
  }

  updatePageBounds(): void {
    this.pageDomRect = this.hostRef.nativeElement.getBoundingClientRect();
  }

  initializeNotes(textboxes: InternalSingleNoteState[]): void {
    this.updatePageBounds();
    this.wrapperViewRef?.clear();
    this.activeNotes = [];
    if (textboxes?.length > 0) {
      textboxes.forEach((note) =>
        this.createElement(this.wrapperViewRef, note, NoteStatus.AlreadySaved, false)
      );
    }
  }

  onMouseDown(
    e: MouseCoords,
    currentZoom: number|null,
    viewContainerRef: ViewContainerRef|undefined,
    bounds: DOMRect|null|undefined
  ): void {
    if (!bounds || !currentZoom) {
      return;
    }
    const distanceFromLeft =
      (this.utilitiesService.percentage(e.x, bounds.width) * currentZoom) / 100;
    const distanceFromTop =
      (this.utilitiesService.percentage(e.y, bounds.height) * currentZoom) / 100;

    const note = {
      distanceFromLeft,
      distanceFromTop,
      content: '',
    };

    this.createElement(
      viewContainerRef,
      {
        note,
      },
      NoteStatus.ReadyForSave,
      true
    );
  }

  private createComponentRef(
    viewContainerRef: ViewContainerRef,
    isMobileApp: boolean,
    currentDeviceOS: string
  ): ComponentRef<AbstractDrawerTextComponent> {
    if (isMobileApp && MOBILE_OPERATING_SYSTEMS.includes(currentDeviceOS as OperatingSystem)) {
      const noteBoxFactory = this.componentFactoryResolver.resolveComponentFactory(DrawerTextMobileComponent)
      return viewContainerRef.createComponent(noteBoxFactory);
    }
    const noteBoxFactory = this.componentFactoryResolver.resolveComponentFactory(DrawerTextComponent)
    return viewContainerRef.createComponent(noteBoxFactory);
  }

  createElement(
    viewContainerRef: ViewContainerRef|undefined,
    internalNote: Subset<InternalSingleNoteState>,
    noteStatus: NoteStatus,
    activeOnInit: boolean
  ): void {
    const { note } = internalNote;
    if (!viewContainerRef || !note) {
      return;
    }

    const id = internalNote.id || Math.random().toString();
    this.textbookActionsHandler.currentDeviceOS$.pipe(filter(Boolean), first()).subscribe((os) => {
      const viewRef = this.createComponentRef(viewContainerRef, this.textbookActionsHandler.isMobileApp, os);
      viewRef.instance.initWith({
        isBoxActive: activeOnInit,
        noteStatus: noteStatus,
        boundsScale: this.boundsScale,
        noteId: id,
        x: note.distanceFromLeft + '%',
        y: note.distanceFromTop + '%',
        content: note.content || '',
        currentClickedTarget$: this.utilitiesService.currentClickedTarget$,
        currentZoom$: this.currentZoom$,
        isOnline$: this.isOnlineMode$,
        textColor: note.fontColor || this.textColor,
        fontSize: note.fontSize || 18,
        supportsTouchDevices: this.utilitiesService.isTouchDevice,
        isVirtualKeyboardSupported: this.utilitiesService.isVirtualKeyboardSupported,
        disabled: this.isDisabled,
      });


      const textContent = viewRef.instance.textContentChange.subscribe(
        (content) => (viewRef.instance.setContent(content))
      );

      const noteFontSize = viewRef.instance.fontSizeChange.subscribe(
        (fontSize) => (viewRef.instance.setFontSize(fontSize))
      );

      const saveSubscription = viewRef.instance.saveNote.subscribe((noteToSave) => {
        viewRef.instance.setNoteId(noteToSave.id);
        viewRef.instance.setNoteStatus(NoteStatus.AlreadySaved);

        this.activeNotes = this.activeNotes.map((internalActiveNote) => ({
          ...internalActiveNote,
          id: internalNote.id === id ? noteToSave.id : internalActiveNote.id,
        }));

        return this.actionNotesService.createNote(noteToSave);
      });

      const updateSubscription = viewRef.instance.updateNote.subscribe((noteToUpdate) => {
        return this.actionNotesService.updateNote(noteToUpdate);
      });

      const mouseEnterSubscription = viewRef.instance.mouseEnter.subscribe(() =>
        this.zoomDisabled.emit(true)
      );
      const mouseLeaveSubscription = viewRef.instance.mouseLeave.subscribe(() =>
        this.zoomDisabled.emit(false)
      );

      const isTextToolActiveSubscription = viewRef.instance.isTextToolActive.subscribe((isActive) =>
        this.isTextToolActive.emit(!isActive)
      );

      const isTextEditedSubscription = viewRef.instance.canvasEdited.subscribe((isEdited) =>
        this.isTextEdited.emit(isEdited)
      );

      const isActiveSubscription = viewRef.instance.isActive.subscribe((isActive) =>
        this.isActive.emit(isActive)
      );

      const hideUnClickedNotesSubscription = viewRef.instance.hideUnClickedNotes.subscribe(
        (clickedNoteId) => {
          this.activeNotes
            .filter(({ id }) => id !== clickedNoteId)
            .map((restNotes) => {
              restNotes.viewRef.instance.setBoxActiveState(false);
            });
        }
      );

      const disableSubscription = this.isDisabled$.subscribe((disabled) => viewRef.instance.setDisabled(disabled));

      const removeSubscription = viewRef.instance.removeNote.subscribe((noteIdToRemove: string) => {
        if (!note) {
          return;
        }

        this.activeNotes = this.activeNotes.filter(
          ({ id: activeNoteId }) => activeNoteId !== noteIdToRemove
        );

        const shouldDelete = viewRef.instance.getNoteStatus() !== NoteStatus.ReadyForSave;

        if (shouldDelete) {
          this.actionNotesService.deleteNote(viewRef.instance.getNoteId());
        }
        const isLastElementToRemove = this.actionNotesService.currentNotes.length === 0;

        isLastElementToRemove && this.actionNotesService.setSaveActionNoteStatus(SaveStatus.Delete);
        viewRef.destroy();
        saveSubscription.unsubscribe();
        updateSubscription.unsubscribe();
        removeSubscription.unsubscribe();
        textContent.unsubscribe();
        noteFontSize.unsubscribe();
        mouseEnterSubscription.unsubscribe();
        mouseLeaveSubscription.unsubscribe();
        isTextToolActiveSubscription.unsubscribe();
        isTextEditedSubscription.unsubscribe();
        hideUnClickedNotesSubscription.unsubscribe();
        disableSubscription.unsubscribe();
        isActiveSubscription.unsubscribe();
      });

      this.activeNotes = [
        {
          viewRef,
          id,
        },
        ...this.activeNotes,
      ];

      this.changeDetector.markForCheck();
    });
  }

  getMousePosition(e: MouseEvent|TouchEvent, element: HTMLElement): { x: number; y: number } {
    let x: number;
    let y: number;

    if (window.TouchEvent && e instanceof TouchEvent) {
      x = e.changedTouches[0].clientX;
      y = e.changedTouches[0].clientY;
    } else {
      const { clientX, clientY } = e as MouseEvent;
      x = clientX;
      y = clientY;
    }

    const { x: boundX, y: boundY, width, height } = element.getBoundingClientRect();

    x = this.utilitiesService.clamp(
      ((x - boundX) / width) * element.offsetWidth,
      0,
      width * this.boundsScale
    );
    y = this.utilitiesService.clamp(
      ((y - boundY) / height) * element.offsetHeight,
      0,
      height * this.boundsScale
    );

    return { x, y };
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
